// vg-binding: P18.D-21
// vg-binding: P18.D-28
//
// apps/vendor/src/features/orders/detail/OrderInfo.tsx
//
// Phase 7 Wave 6 Task 20 — order summary card on the detail page.
// Wave 6 ships the canonical metadata block (ids + status + SLA chip);
// the Order $lookup for buyer/product/qty/ship-address lands in Wave 9.
//
// Phase 18 Task 34 — "a machine may be driving this row"
// ------------------------------------------------------
// A vendor that integrated its own production system can have an assignment
// acked and advanced by software while a human stares at this card. The card
// therefore states it: an external-ack timestamp and a badge, so an unexpected
// status change reads as "our system did that" instead of "the portal is
// broken" or "a colleague did it" (P18.D-01: no machine action is ever
// attributed to a person).
//
// The evidence has two sources, in order of authority:
//   1. `detail.external_ack_at` — the set-once column task-21 stamps on ack.
//      `getOrderItemDetail` projects it as of 5bfb2c1b6; it stays optional here
//      because it is forward-only with no backfill, so every assignment that
//      predates the machine lane reads null forever.
//   2. the transition log — a row whose actor role is `vendor_api` is a write
//      that arrived through the machine lane, which is the same claim with a
//      different timestamp. `useOrderTransitions` is already fetched by both
//      parents (drawer + full page) under the same query key, so this is a
//      cache read, not a second request.
//
// Covers goal: G-27

import { cn, VendorStatusBadge } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';
import { STATUS_PILL_CLASS } from '../../../lib/ui/statusPill.js';
import {
  useOrderTransitions,
  type OrderItemDetail,
  type OrderStatus,
  type SlaColorTier,
} from '../api.js';

/**
 * Wire status -> i18n key, in the `orders` namespace this component already
 * uses. The enum VALUE stays English on the wire (it is the contract); only the
 * LABEL is translated.
 *
 * This row used to render `detail.status` raw, so a vendor read `in_production`
 * in the middle of a `<dl>` whose neighbours were translated — and phase 18
 * added two more translated rows either side of it, which made the odd one out
 * plainer. The `statusLabel.*` keys already existed for the print-queue card
 * and already cover every member of this union.
 */
const STATUS_LABEL_KEY: Record<OrderStatus, string> = {
  assigned: 'statusLabel.assigned',
  accepted: 'statusLabel.accepted',
  in_production: 'statusLabel.inProduction',
  quality_check: 'statusLabel.qualityCheck',
  ready_to_ship: 'statusLabel.readyToShip',
  shipped: 'statusLabel.shipped',
  pending_reassignment: 'statusLabel.pendingReassignment',
  completed: 'statusLabel.completed',
};

const CHIP: Record<SlaColorTier, string> = {
  green: STATUS_PILL_CLASS.success,
  yellow: STATUS_PILL_CLASS.progress,
  orange: STATUS_PILL_CLASS.warning,
  red: STATUS_PILL_CLASS.danger,
};

function formatTimestamp(ms: number): string {
  return new Date(ms).toLocaleString(undefined);
}

interface Props {
  detail: OrderItemDetail;
}

export function OrderInfo({ detail }: Props): JSX.Element {
  const { t } = useTranslation('orders');

  // Cache read — both parents already hold this query (same key).
  const transitionsQ = useOrderTransitions(detail.order_id, detail.item_id);
  const machineRows = (transitionsQ.data?.rows ?? []).filter(
    (r) => r.transitioned_by_role === 'vendor_api',
  );
  const lastMachineAt = machineRows.reduce<number | null>(
    (acc, r) => (acc === null || r.occurred_at > acc ? r.occurred_at : acc),
    null,
  );
  const externalAckAt = detail.external_ack_at ?? null;
  const machineDriven = externalAckAt !== null || machineRows.length > 0;

  const backOrderValue = detail.back_order_used
    ? t('orderInfo.backOrderUsed', {
        at: detail.back_order_used_at
          ? formatTimestamp(detail.back_order_used_at)
          : t('orderInfo.backOrderUnknown'),
      })
    : detail.back_order_enabled
      ? t('orderInfo.backOrderAllowed')
      : t('orderInfo.backOrderNotAllowed');

  return (
    <section className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-pw-text">
            Order #{detail.order_id.slice(-8)}
          </h2>
          <p className="mt-1 font-mono text-xs text-pw-text-muted">
            item: {detail.item_id}
          </p>
          {machineDriven ? (
            <p className="mt-2">
              <VendorStatusBadge
                status="external_system"
                label={t('orderInfo.managedByExternal')}
                testId="order-detail-external-badge"
              />
            </p>
          ) : null}
        </div>
        <span
          className={cn(
            'inline-flex items-center rounded px-2 py-1 text-xs font-semibold',
            CHIP[detail.sla_color_tier],
          )}
        >
          SLA {detail.sla_percent_used}%
        </span>
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
        <Row label={t('orderInfo.labelStatus')} value={t(STATUS_LABEL_KEY[detail.status])} />
        <Row label={t('orderInfo.labelItemVersion')} value={`v${detail.item_version}`} />
        <Row label={t('orderInfo.labelAssignedAt')} value={formatTimestamp(detail.assigned_at)} />
        <Row
          label={t('orderInfo.labelAcceptedAt')}
          value={detail.accepted_at ? formatTimestamp(detail.accepted_at) : '—'}
        />
        <Row label={t('orderInfo.labelSlaOriginal')} value={formatTimestamp(detail.sla_deadline)} />
        <Row label={t('orderInfo.labelSlaEffective')} value={formatTimestamp(detail.effective_deadline)} />
        <Row label={t('orderInfo.labelBackOrder')} value={backOrderValue} />
        {machineDriven ? (
          <>
            <Row
              label={t('orderInfo.labelExternalAck')}
              value={
                externalAckAt !== null
                  ? formatTimestamp(externalAckAt)
                  : lastMachineAt !== null
                    ? formatTimestamp(lastMachineAt)
                    : '—'
              }
            />
            <Row
              label={t('orderInfo.labelExternalSource')}
              value={
                externalAckAt !== null
                  ? t('orderInfo.externalSourceAck')
                  : t('orderInfo.externalSourceTransition')
              }
            />
          </>
        ) : null}
      </dl>

      <div className="mt-4 rounded-md border border-pw-border bg-pw-surface px-3 py-2 text-xs text-pw-text">
        <p className="mb-1 font-semibold uppercase tracking-wider text-pw-text-muted">
          {t('orderInfo.sectionSummary')}
        </p>
        <dl className="flex flex-col gap-0.5">
          <Row
            label={t('orderInfo.labelOrderCode')}
            value={detail.order_summary.order_number ?? '—'}
          />
          <Row
            label={t('orderInfo.labelQuantity')}
            value={
              typeof detail.order_summary.quantity === 'number'
                ? String(detail.order_summary.quantity)
                : '—'
            }
          />
          <Row
            label={t('orderInfo.labelRecipient')}
            value={detail.order_summary.recipient_name ?? '—'}
          />
          {detail.order_summary.ship_to ? (
            <Row
              label={t('orderInfo.labelAddress')}
              value={[
                detail.order_summary.ship_to.line1,
                detail.order_summary.ship_to.line2,
                detail.order_summary.ship_to.city,
                detail.order_summary.ship_to.state,
                detail.order_summary.ship_to.postal,
                detail.order_summary.ship_to.country,
              ]
                .filter((s): s is string => Boolean(s))
                .join(', ') || '—'}
            />
          ) : null}
        </dl>
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex justify-between gap-3 py-1">
      <dt className="text-pw-text-muted">{label}</dt>
      <dd className="font-medium text-pw-text">{value}</dd>
    </div>
  );
}

export default OrderInfo;
